import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ModalService } from '@features/modals/services/modal.service';
import { ToastrService } from 'ngx-toastr';
import { AdminItemsService } from '../../admin-items/admin-items.service';
import { FormFieldComponent } from '../../../../core/components/form-field/form-field.component';
import { AdminItem } from '@features/admin/admin-items/admin-item.interface';

@Component({
  selector: 'app-admin-item-modal',
  imports: [FormFieldComponent, ReactiveFormsModule],
  templateUrl: './admin-item-modal.component.html',
  styleUrl: './admin-item-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  host: {
    class: 'modal-container flex flex-col',
  },
})
export class AdminItemModalComponent {
  private readonly toastrService = inject(ToastrService);
  private readonly modalService = inject(ModalService);
  public readonly adminItemsService = inject(AdminItemsService);

  item = input<AdminItem>();

  isAdd = computed(() => !this.item());

  name = new FormControl<string>('', [Validators.required]);
  price = new FormControl<number | null>(null, [Validators.required]);

  itemForm = new FormGroup({
    name: this.name,
    price: this.price,
  });

  constructor() {
    effect(() => {
      if (this.item()) {
        this.itemForm.patchValue({
          name: this.item()?.name,
          price: this.item()?.price,
        });
      }
    });
  }

  async onSubmit() {
    this.itemForm.markAllAsTouched();

    if (!this.itemForm.valid) {
      this.toastrService.error('Form is not valid. Please check the inputs.');
      return;
    }

    const name = this.itemForm.get('name')?.value;
    const price = this.itemForm.get('price')?.value;

    if (!name) {
      this.toastrService.error('Name is required');
      return;
    }

    if (!price) {
      this.toastrService.error('Price is required');
      return;
    }

    try {
      await this.adminItemsService.editOrAddItem({
        name,
        price: Number(price),
      });
      this.toastrService.success(
        `Item has been ${this.isAdd() ? 'added' : 'edited'} successfully`
      );
      this.modalService.close();
    } catch (error) {
      if (error instanceof Error) {
        this.itemForm.setErrors({
          serverError: error.message,
        });
        this.toastrService.error(
          `Failed to add/edit item. Please try again. Message: ${error.message}`,
          'Error'
        );
      } else {
        this.toastrService.error(
          `Failed to add/edit item. Please try again. Message: ${error}`,
          'Error'
        );
      }
    }
  }
}
